// OAIP-153 — INTERIM / THROWAWAY: one-click OAuth consent screen.
// Replaced when Apex moves to AWS + Entra ID SSO (the consent + the human login
// behind it shift to Entra). The MCP client (e.g. Claude Desktop) sends the user
// here from /oauth/authorize; this page authenticates the human via the EXISTING
// Firebase login, shows what's being granted, and POSTs the decision back to
// apexOauthHandler, which mints the auth code and returns the redirect URL.
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  App,
  Button,
  Card,
  Flex,
  Form,
  Input,
  Space,
  Spin,
  Typography,
} from "antd";
import type { InputRef } from "antd";
import type { FirebaseError } from "firebase/app";
import { useAuth } from "../lib/auth";
import { approveOauth } from "../lib/api";
import type { OauthConsentParams } from "../lib/api";
import { DIT_LOGO_URL, DIT_LOGO_ALT } from "../lib/dit";

const PRIMARY = "#092940";

function mapFirebaseError(err: unknown): string {
  const code = (err as FirebaseError)?.code;
  switch (code) {
    case "auth/invalid-credential":
    case "auth/wrong-password":
    case "auth/user-not-found":
      return "Invalid email or password";
    case "auth/too-many-requests":
      return "Too many attempts — try again later";
    case "auth/user-disabled":
      return "Your account is disabled — contact an admin";
    default:
      return (err as Error)?.message || "Sign-in failed";
  }
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <Flex
      align="center"
      justify="center"
      style={{ width: "100%", minHeight: "calc(100vh - 48px)", padding: "40px 24px" }}
    >
      <Card style={{ width: 440, boxShadow: "0 12px 40px rgba(9, 41, 64, 0.12)" }}>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Space
            direction="vertical"
            size="small"
            style={{ width: "100%", alignItems: "center" }}
          >
            <img
              src={DIT_LOGO_URL}
              alt={DIT_LOGO_ALT}
              style={{ height: 48, display: "block", margin: "0 auto 8px" }}
            />
            <Typography.Title level={3} style={{ textAlign: "center", marginBottom: 0, color: PRIMARY }}>
              Connect to Apex
            </Typography.Title>
          </Space>
          {children}
        </Space>
      </Card>
    </Flex>
  );
}

export default function OAuthConsent() {
  const [searchParams] = useSearchParams();
  const { user, loading, allowlisted, role, signIn, signOutUser } = useAuth();
  const { message } = App.useApp();

  const [signingIn, setSigningIn] = useState(false);
  const [signInError, setSignInError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<null | "approve" | "deny">(null);

  const approveRef = useRef<HTMLButtonElement>(null);
  const emailRef = useRef<InputRef>(null);

  // Authorize params handed off from /oauth/authorize.
  const params: OauthConsentParams = {
    client_id: searchParams.get("client_id") || "",
    redirect_uri: searchParams.get("redirect_uri") || "",
    code_challenge: searchParams.get("code_challenge") || "",
    state: searchParams.get("state") || "",
    scope: searchParams.get("scope") || "",
    resource: searchParams.get("resource") || "",
  };
  const clientName = searchParams.get("client_name") || "An MCP client";
  const paramsValid = !!(params.client_id && params.redirect_uri && params.code_challenge);

  const canConsent = !!user && allowlisted && paramsValid;

  // Move keyboard focus to the most relevant control as state settles, so
  // keyboard + screen-reader users land on the actionable element.
  useEffect(() => {
    if (loading) return;
    if (!user) emailRef.current?.focus();
    else if (canConsent) approveRef.current?.focus();
  }, [loading, user, canConsent]);

  const handleSignIn = async (values: { email: string; password: string }) => {
    setSigningIn(true);
    setSignInError(null);
    try {
      await signIn(values.email.trim().toLowerCase(), values.password);
    } catch (err) {
      setSignInError(mapFirebaseError(err));
    } finally {
      setSigningIn(false);
    }
  };

  const decide = async (approve: boolean) => {
    setSubmitting(approve ? "approve" : "deny");
    try {
      const { redirect } = await approveOauth(params, approve);
      // Hand control back to the MCP client (loopback URL or custom scheme).
      window.location.assign(redirect);
    } catch (err) {
      message.error((err as Error)?.message || "Could not complete the request");
      setSubmitting(null);
    }
  };

  if (!paramsValid) {
    return (
      <Shell>
        <Alert
          type="error"
          showIcon
          message="Invalid authorization request"
          description="This link is missing required parameters. Start the connection again from your MCP client (e.g. Claude Desktop)."
        />
      </Shell>
    );
  }

  if (loading) {
    return (
      <Shell>
        <Flex justify="center" style={{ padding: 24 }}>
          <Spin aria-label="Loading" tip={<Typography.Text>Loading…</Typography.Text>} />
        </Flex>
      </Shell>
    );
  }

  // Not signed in → authenticate the human with the existing Firebase login.
  if (!user) {
    return (
      <Shell>
        <Typography.Paragraph style={{ textAlign: "center", marginBottom: 0 }}>
          <strong>{clientName}</strong> wants to connect to Apex. Sign in to continue.
        </Typography.Paragraph>
        {signInError && <Alert type="error" message={signInError} showIcon />}
        <Form layout="vertical" onFinish={handleSignIn}>
          <Form.Item
            name="email"
            label="Email"
            rules={[{ required: true, type: "email", message: "Enter a valid email" }]}
          >
            <Input ref={emailRef} placeholder="you@nc.gov" autoComplete="username" />
          </Form.Item>
          <Form.Item
            name="password"
            label="Password"
            rules={[{ required: true, message: "Password is required" }]}
          >
            <Input.Password placeholder="Password" autoComplete="current-password" />
          </Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            block
            loading={signingIn}
            style={{ background: PRIMARY, borderColor: PRIMARY }}
          >
            Sign in
          </Button>
        </Form>
      </Shell>
    );
  }

  // Signed in but not authorized for Apex.
  if (!allowlisted) {
    return (
      <Shell>
        <Alert
          type="warning"
          showIcon
          message="Account not authorized"
          description={`${user.email} is not on the Apex access list, so it can't be connected. Contact an OAIP admin for access.`}
        />
        <Button block onClick={() => signOutUser()} aria-label="Sign out and use a different account">
          Sign out
        </Button>
      </Shell>
    );
  }

  // Consent.
  return (
    <Shell>
      <Typography.Paragraph style={{ marginBottom: 0 }}>
        <strong>{clientName}</strong> wants to connect to Apex as{" "}
        <strong>{user.email}</strong>
        {role ? ` (role: ${role})` : ""}.
      </Typography.Paragraph>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
        It will be able to read and act on Apex on your behalf, limited to your role.
        Reads are free; the client confirms each write with you, and irreversible or
        account actions stay in the Apex web app.
      </Typography.Paragraph>
      <Space style={{ width: "100%", justifyContent: "flex-end" }}>
        <Button
          onClick={() => decide(false)}
          loading={submitting === "deny"}
          disabled={submitting !== null}
          aria-label="Deny the connection request"
        >
          Deny
        </Button>
        <Button
          ref={approveRef}
          type="primary"
          onClick={() => decide(true)}
          loading={submitting === "approve"}
          disabled={submitting !== null}
          style={{ background: PRIMARY, borderColor: PRIMARY }}
          aria-label={`Approve connecting ${clientName} to Apex`}
        >
          Approve
        </Button>
      </Space>
    </Shell>
  );
}
